"use client";

import { useState } from "react";
import { trackLead } from "@/lib/analytics";

interface EmailGateProps {
  toolSlug: string;
  toolType: "quiz" | "calculator";
  headline?: string;
  description?: string;
  buttonText?: string;
  onSubmit?: (email: string, extra?: Record<string, string>) => void;
  collectName?: boolean;
  variant?: "light" | "dark";
  resultData?: {
    score?: number | null;
    level?: string | null;
    metadata?: Record<string, any> | null;
  };
}

export function EmailGate({
  toolSlug,
  toolType,
  headline = "Få resultatene + handlingsplan på e-post",
  description = "Vi sender deg en oppsummering med konkrete neste steg basert på resultatet ditt.",
  buttonText = "Send meg resultatene",
  onSubmit,
  collectName = false,
  variant = "dark",
  resultData,
}: EmailGateProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Ugyldig e-postadresse");
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const metadata = {
        ...(resultData?.metadata || {}),
        source_url: typeof window !== "undefined" ? window.location.href : null,
      };
      const r = await fetch("/api/tool-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool_slug: toolSlug,
          tool_type: toolType,
          email,
          name: name || null,
          score: resultData?.score ?? null,
          level: resultData?.level ?? null,
          metadata,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Kunne ikke lagre");
      }
      setDone(true);
      trackLead("generate_lead", { email, tool_slug: toolSlug, tool_type: toolType, method: "tool-gate" });
      onSubmit?.(email, name ? { name } : undefined);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    const okClass =
      variant === "dark"
        ? "bg-green-500/20 border-green-400 text-green-100"
        : "bg-green-50 border-green-300 text-green-900";
    return (
      <div className={`border rounded-2xl px-5 py-4 text-sm ${okClass}`}>
        ✓ Vi sender resultatene til <strong>{email}</strong> innen 1 minutt.
      </div>
    );
  }

  const isDark = variant === "dark";

  return (
    <div
      className={
        isDark
          ? "bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-3xl p-7"
          : "bg-amber-50 border border-amber-200 rounded-3xl p-6"
      }
    >
      <h3
        className={`text-xl md:text-2xl font-light tracking-tight mb-2 ${isDark ? "" : "text-amber-900"}`}
      >
        {headline}
      </h3>
      <p
        className={`text-sm mb-5 ${isDark ? "text-stone-300" : "text-amber-800"}`}
      >
        {description}
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        {collectName && (
          <input
            type="text"
            placeholder="Fornavn"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={
              isDark
                ? "w-full bg-white/10 border border-white/20 text-white placeholder:text-stone-400 px-4 py-3 rounded-xl focus:outline-none focus:border-white"
                : "w-full bg-white border border-amber-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500"
            }
          />
        )}
        <div className="flex gap-2">
          <input
            type="email"
            required
            placeholder="din@epost.no"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={
              isDark
                ? "flex-1 bg-white/10 border border-white/20 text-white placeholder:text-stone-400 px-4 py-3 rounded-xl focus:outline-none focus:border-white"
                : "flex-1 bg-white border border-amber-200 px-4 py-3 rounded-xl focus:outline-none focus:border-amber-500"
            }
          />
          <button
            type="submit"
            disabled={submitting}
            className={
              isDark
                ? "bg-white text-stone-900 px-6 py-3 rounded-xl font-medium hover:bg-stone-100 disabled:opacity-50 transition-colors whitespace-nowrap"
                : "bg-stone-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-stone-800 disabled:opacity-50 transition-colors whitespace-nowrap"
            }
          >
            {submitting ? "Sender..." : buttonText}
          </button>
        </div>
        {error && (
          <p className={`text-xs ${isDark ? "text-red-300" : "text-red-700"}`}>{error}</p>
        )}
        <p
          className={`text-xs ${isDark ? "text-stone-400" : "text-amber-700"}`}
        >
          Vi sender ingen markedsføring du ikke har bedt om. Du kan slette dataene når som helst.
        </p>
      </form>
    </div>
  );
}
